import {
  PACKAGING_EPR_MATERIALS,
  type PackagingEprMaterial,
  type PackagingShipmentEprPackedItem,
  type PackagingShipmentEprWeights,
} from '@/shared/hooks/api/packagingShipments'

export type EprPackagingGroupUser = {
  packedItemId: string
  packedItemName: string
  sku: string
}

export type EprPackagingGroupComponent = {
  name: string
  material: PackagingEprMaterial
  unitGrams: number | null
  grams: number | null
}

export type EprPackagingGroup = {
  id: string
  name: string
  supplierId: string
  supplierName: string
  users: EprPackagingGroupUser[]
  weights: PackagingShipmentEprWeights
  components: EprPackagingGroupComponent[]
}

function totalGrams(weights: PackagingShipmentEprWeights): number {
  let total = 0
  for (const material of PACKAGING_EPR_MATERIALS) {
    const grams = weights[material]
    if (grams != null && grams > 0) {
      total += grams
    }
  }
  return total
}

function scaleWeights(
  weights: PackagingShipmentEprWeights,
  count: number
): PackagingShipmentEprWeights {
  const scaled: PackagingShipmentEprWeights = {}
  for (const material of PACKAGING_EPR_MATERIALS) {
    const grams = weights[material]
    if (grams != null && grams > 0) {
      scaled[material] = grams * count
    }
  }
  return scaled
}

export function groupEprByPackagingItem(
  packedItems: PackagingShipmentEprPackedItem[]
): EprPackagingGroup[] {
  const groups: EprPackagingGroup[] = []
  const byId = new Map<string, EprPackagingGroup>()
  for (const packedItem of packedItems) {
    for (const packagingItem of packedItem.packagingItems) {
      let group = byId.get(packagingItem.id)
      if (group == null) {
        group = {
          id: packagingItem.id,
          name: packagingItem.name,
          supplierId: packagingItem.supplierId,
          supplierName: packagingItem.supplierName,
          users: [],
          weights: packagingItem.weights,
          components: packagingItem.components.map((component) => ({
            name: component.name,
            material: component.material,
            unitGrams: component.grams,
            grams: component.grams,
          })),
        }
        byId.set(packagingItem.id, group)
        groups.push(group)
      }
      group.users.push({
        packedItemId: packedItem.id,
        packedItemName: packedItem.name,
        sku: packedItem.sku,
      })
    }
  }

  for (const group of groups) {
    const count = group.users.length
    group.weights = scaleWeights(group.weights, count)
    for (const component of group.components) {
      if (component.unitGrams != null) {
        component.grams = component.unitGrams * count
      }
    }
  }

  return groups.slice().sort((left, right) => {
    const byWeight = totalGrams(right.weights) - totalGrams(left.weights)
    if (byWeight !== 0) {
      return byWeight
    }
    return left.name.localeCompare(right.name)
  })
}

export function packagingGroupMatchesQuery(
  group: EprPackagingGroup,
  query: string
): boolean {
  if (group.name.toLocaleLowerCase().includes(query)) {
    return true
  }
  return group.users.some(
    (user) =>
      user.sku.toLocaleLowerCase().includes(query) ||
      user.packedItemName.toLocaleLowerCase().includes(query)
  )
}
